import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc, addDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import ConversationView from './ConversationView';
import { getAnonymousDisplayName, getEmojiForUserId } from '../utils/emojiUtils';
import { useLocation } from 'react-router-dom';
import { FaSearch, FaTrash, FaTimes, FaSortAmountDown, FaSortAmountUp } from 'react-icons/fa';
import { Avatar } from '../components/ds/core/Avatar';
import { Input } from '../components/ds/core/Input';
import { isoToFlag } from '../components/globe/CountryTag';
import './MessagesView.css';

function MessagesView({ clearUnreadNotification }) {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasOpenedFriend, setHasOpenedFriend] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteMode, setDeleteMode] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('recent');
  const userId = localStorage.getItem('userId');
  const location = useLocation();

  useEffect(() => {
    loadConversations();
    // Clear unread notification when viewing messages
    if (clearUnreadNotification) {
      clearUnreadNotification();
    }
  }, []);

  useEffect(() => {
    // Check if there's a userId query parameter
    const params = new URLSearchParams(location.search);
    const friendUserId = params.get('userId');

    if (friendUserId && !loading && !hasOpenedFriend) {
      setHasOpenedFriend(true);
      openConversationWithFriend(friendUserId);
    }
  }, [conversations, location.search, loading, hasOpenedFriend]);

  const loadConversations = async () => {
    try {
      // Get all conversations where user is a participant
      const conversationsQuery = query(
        collection(db, 'conversations'),
        where('participants', 'array-contains', userId)
      );

      const conversationsSnapshot = await getDocs(conversationsQuery);

      // Batch fetch unread messages once for unread badges
      const unreadByConv = new Map();
      try {
        const unreadSnapshot = await getDocs(query(
          collection(db, 'messages'),
          where('receiverId', '==', userId),
          where('read', '==', false)
        ));
        unreadSnapshot.docs.forEach(msgDoc => {
          const convId = msgDoc.data().conversationId;
          unreadByConv.set(convId, (unreadByConv.get(convId) || 0) + 1);
        });
      } catch (err) {
        console.error('Error loading unread counts:', err);
      }

      const conversationsList = [];

      for (const convDoc of conversationsSnapshot.docs) {
        const convData = convDoc.data();
        const otherUserId = convData.participants.find(id => id !== userId);

        // Get other user data
        const otherUserDoc = await getDoc(doc(db, 'users', otherUserId));
        const otherUserData = otherUserDoc.data();

        // Check if friends
        const friendshipQuery = query(
          collection(db, 'friendships'),
          where('participants', 'array-contains', userId)
        );
        const friendshipSnapshot = await getDocs(friendshipQuery);
        const isFriend = friendshipSnapshot.docs.some(doc =>
          doc.data().participants.includes(otherUserId) && doc.data().status === 'accepted'
        );

        conversationsList.push({
          id: convDoc.id,
          ...convData,
          otherUser: {
            id: otherUserId,
            username: otherUserData?.username || null,
            countryCode: otherUserData?.countryCode || null,
          },
          areFriends: isFriend,
          unreadCount: unreadByConv.get(convDoc.id) || 0
        });
      }

      // Sort by last message time
      conversationsList.sort((a, b) => {
        const timeA = a.lastMessageTime?.seconds || 0;
        const timeB = b.lastMessageTime?.seconds || 0;
        return timeB - timeA;
      });

      setConversations(conversationsList);
      setLoading(false);
    } catch (error) {
      console.error('Error loading conversations:', error);
      setLoading(false);
    }
  };

  const openConversationWithFriend = async (friendUserId) => {
    try {
      // Check if conversation already exists
      const existingConv = conversations.find(conv => conv.otherUser.id === friendUserId);

      if (existingConv) {
        // Open existing conversation
        setSelectedConversation(existingConv.id);
      } else {
        // Create new conversation
        const newConversationData = {
          participants: [userId, friendUserId],
          lastMessage: '',
          lastMessageTime: serverTimestamp(),
          updatedAt: serverTimestamp(),
          createdAt: serverTimestamp()
        };

        const newConvRef = await addDoc(collection(db, 'conversations'), newConversationData);

        // Open the new conversation
        setSelectedConversation(newConvRef.id);
      }
    } catch (error) {
      console.error('Error opening conversation:', error);
      alert('Failed to open conversation: ' + error.message);
    }
  };

  const toggleSelectConversation = (convId) => {
    setSelectedForDelete(prev => {
      const next = new Set(prev);
      if (next.has(convId)) {
        next.delete(convId);
      } else {
        next.add(convId);
      }
      return next;
    });
  };

  const handleDeleteSelected = async () => {
    if (selectedForDelete.size === 0) return;
    if (!window.confirm(`Delete ${selectedForDelete.size} conversation(s)? This cannot be undone.`)) return;

    setDeleting(true);
    try {
      for (const convId of selectedForDelete) {
        // Delete all messages in this conversation
        const messagesQuery = query(
          collection(db, 'messages'),
          where('conversationId', '==', convId)
        );
        const messagesSnapshot = await getDocs(messagesQuery);
        for (const msgDoc of messagesSnapshot.docs) {
          await deleteDoc(doc(db, 'messages', msgDoc.id));
        }

        // Delete the conversation
        await deleteDoc(doc(db, 'conversations', convId));
      }

      // If the open conversation was deleted, close it
      if (selectedConversation && selectedForDelete.has(selectedConversation)) {
        setSelectedConversation(null);
      }
      setSelectedForDelete(new Set());
      setDeleteMode(false);
      await loadConversations();
    } catch (error) {
      console.error('Error deleting conversations:', error);
      alert('Failed to delete some conversations');
    } finally {
      setDeleting(false);
    }
  };

  const filteredConversations = conversations
    .filter(conv => {
      const displayName = conv.areFriends ? conv.otherUser.username : getAnonymousDisplayName(conv.otherUser.id);
      const matchesSearch = displayName?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory =
        categoryFilter === 'all' ||
        (categoryFilter === 'friends' ? conv.areFriends : !conv.areFriends);
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      const ta = a.lastMessageTime?.seconds || 0;
      const tb = b.lastMessageTime?.seconds || 0;
      return sortOrder === 'recent' ? tb - ta : ta - tb;
    });

  const renderConversationItem = (conv) => {
    const isActive = conv.id === selectedConversation;
    const name = conv.areFriends ? conv.otherUser.username : getAnonymousDisplayName(conv.otherUser.id);
    return (
      <div
        key={conv.id}
        className={`conv-item ${isActive ? 'active' : ''} ${deleteMode && selectedForDelete.has(conv.id) ? 'selected-for-delete' : ''}`}
        onClick={() => deleteMode ? toggleSelectConversation(conv.id) : setSelectedConversation(conv.id)}
      >
        {deleteMode && (
          <input
            type="checkbox"
            className="delete-checkbox"
            checked={selectedForDelete.has(conv.id)}
            onChange={() => toggleSelectConversation(conv.id)}
            onClick={(e) => e.stopPropagation()}
          />
        )}
        <Avatar
          emoji={conv.areFriends ? undefined : getEmojiForUserId(conv.otherUser.id)}
          name={name}
          kind={conv.areFriends ? 'friend' : 'anonymous'}
          size={42}
        />
        <div className="conv-item-info">
          <div className="conv-item-top">
            <span className="conv-item-name">{name}</span>
            {conv.otherUser.countryCode && (
              <span className="conv-item-flag">{isoToFlag(conv.otherUser.countryCode)}</span>
            )}
          </div>
          <div className="conv-item-preview">
            {conv.lastMessage || 'No messages yet'}
          </div>
        </div>
        {!deleteMode && conv.unreadCount > 0 && <span className="conv-item-unread" />}
      </div>
    );
  };

  return (
    <div className="messages-page">
      <div className={`mv-panel ${selectedConversation ? 'has-active' : ''}`}>
        {/* List pane */}
        <div className="messages-list-pane">
          <div className="list-pane-header">
            <div className="list-pane-title">
              <div className="list-pane-heading">Conversations</div>
              <div className="list-pane-count">{conversations.length} conversation{conversations.length === 1 ? '' : 's'}</div>
            </div>
            {conversations.length > 0 && (
              deleteMode ? (
                <button
                  className="list-header-btn"
                  onClick={() => { setDeleteMode(false); setSelectedForDelete(new Set()); }}
                  title="Cancel"
                >
                  <FaTimes />
                </button>
              ) : (
                <button
                  className="list-header-btn"
                  onClick={() => setDeleteMode(true)}
                  title="Delete conversations"
                >
                  <FaTrash />
                </button>
              )
            )}
          </div>

          {conversations.length > 0 && (
            <div className="list-pane-search">
              <Input
                icon={<FaSearch />}
                placeholder="Search conversations…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  background: '#fff',
                  color: 'var(--text-strong)',
                  border: '1px solid var(--border-strong)',
                }}
              />
            </div>
          )}

          {conversations.length > 0 && (
            <div className="list-filters">
              <div className="filter-pills">
                {[['all', 'All'], ['anonymous', 'Anonymous'], ['friends', 'Friends']].map(([key, label]) => (
                  <button
                    key={key}
                    className={`filter-pill ${categoryFilter === key ? 'active' : ''}`}
                    onClick={() => setCategoryFilter(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <button
                className="sort-toggle"
                onClick={() => setSortOrder(o => (o === 'recent' ? 'oldest' : 'recent'))}
                title={sortOrder === 'recent' ? 'Newest first' : 'Oldest first'}
              >
                {sortOrder === 'recent' ? <FaSortAmountDown /> : <FaSortAmountUp />}
                {sortOrder === 'recent' ? 'Newest' : 'Oldest'}
              </button>
            </div>
          )}

          {deleteMode && (
            <div className="delete-bar">
              <button
                className="delete-selected-btn"
                onClick={handleDeleteSelected}
                disabled={selectedForDelete.size === 0 || deleting}
              >
                <FaTrash /> {deleting ? 'Deleting…' : `Delete (${selectedForDelete.size})`}
              </button>
            </div>
          )}

          <div className="conv-list">
            {loading ? (
              <div className="conv-list-empty">Loading conversations…</div>
            ) : conversations.length === 0 ? (
              <div className="conv-list-empty">
                No conversations yet. Send a message from the Globe to start chatting!
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="conv-list-empty">No conversations match your search.</div>
            ) : (
              filteredConversations.map(renderConversationItem)
            )}
          </div>
        </div>

        {/* Thread pane */}
        <div className="messages-thread-pane">
          {selectedConversation ? (
            <ConversationView
              conversationId={selectedConversation}
              onBack={() => {
                setSelectedConversation(null);
                loadConversations();
              }}
            />
          ) : (
            <div className="thread-empty">
              <div className="thread-empty-emoji">💬</div>
              <div className="thread-empty-title">Your conversations</div>
              <div className="thread-empty-text">Pick a conversation on the left to read it here.</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MessagesView;
